import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowDown,
  ArrowUp,
  ChevronRight,
  Ellipsis,
  EyeOff,
  GripVertical,
  Pencil,
  Plus,
  Tag as TagIcon,
  Trash2,
  Settings2,
} from '@/components/ui/icons';
import type { Location, Task, View, ViewColumn } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { getDb, useDb, useStore } from '@/lib/store';
import { effectiveStatuses, fieldsForLocation, statusOf } from '@/lib/hierarchy';
import { subtaskCount, type TaskGroup } from '@/lib/query';
import {
  createTask,
  deleteTask,
  duplicateTask,
  reorderTask,
  updateTask,
  updateViewSettings,
} from '@/lib/actions';
import { useOpenTask } from '@/lib/nav';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { openContextMenu, toast } from '@/components/ui/overlays';
import { Tooltip } from '@/components/ui/Tooltip';
import { Avatar } from '@/components/ui/Avatar';
import { Icon } from '@/components/ui/Icon';
import { StatusIcon, StatusMenu, StatusPill, statusProgress } from '@/components/fields/Status';
import { TagMenu } from '@/components/fields/Tags';
import { TaskTypeIcon } from '@/components/fields/TaskCells';
import { PeopleMenu } from '@/components/fields/People';
import { PriorityFlag, PriorityMenu } from '@/components/fields/Priority';
import { DateRangeMenu } from '@/components/fields/DatePicker';
import { openDialog } from '@/shell/dialogs';
import { ViewToolbar } from './ViewToolbar';
import { FieldsPanel } from './FieldsPanel';
import { Cell, resolveColumns, type ColumnDef } from './cells';
import { useViewQuery } from './useViewQuery';
import { MoveTaskMenu } from './MoveTaskMenu';

import { TaskContextMenu } from '@/task/TaskContextMenu';

// ---------------------------------------------------------------------------
// Inline task creation row

function InlineAdd({
  group,
  location,
  parentId,
  listId,
  depth,
  gridTemplate,
  onDone,
  placeholder = 'Task Name or type \'/\' for commands',
}: {
  group?: TaskGroup;
  location: Location;
  parentId?: string | null;
  listId?: string;
  depth: number;
  gridTemplate: string;
  onDone: () => void;
  placeholder?: string;
}) {
  const [name, setName] = useState('');
  const [assignees, setAssignees] = useState<string[]>(group?.defaults.assigneeIds ?? []);
  const [due, setDue] = useState<number | null>(null);
  const [priority, setPriority] = useState<Task['priority']>(group?.defaults.priority ?? null);
  const ref = useRef<HTMLInputElement>(null);
  const firstListId = useDb((db) => {
    if (listId) return listId;
    if (group?.defaults.listId) return group.defaults.listId as string;
    if (location.type === 'list') return location.id;
    const lists = Object.values(db.lists)
      .filter((l) => !l.archived && (location.type === 'folder' ? l.folderId === location.id : location.type === 'space' ? l.spaceId === location.id : db.spaces[l.spaceId]?.workspaceId === location.id))
      .sort((a, b) => a.orderIndex - b.orderIndex);
    return lists[0]?.id;
  });
  useEffect(() => ref.current?.focus(), []);
  const save = () => {
    if (!name.trim() || !firstListId) return;
    const db = getDb();
    let statusId = group?.defaults.statusId;
    if (!statusId && group?.status) {
      // status groups outside a single list: map by name onto the target list's workflow
      statusId = effectiveStatuses(db, firstListId).find((s) => s.name.toLowerCase() === group.status!.name.toLowerCase())?.id;
    }
    createTask({
      listId: firstListId,
      name,
      parentId: parentId ?? null,
      statusId,
      assigneeIds: assignees,
      dueDate: due,
      priority,
      taskTypeId: (group?.defaults.taskTypeId as string | null | undefined) ?? null,
      tagIds: group?.defaults.tagIds,
      customFields: group?.defaults.customFields,
    });
    setName('');
    setDue(null);
  };
  return (
    <div className="lv-row lv-add-row editing" style={{ gridTemplateColumns: gridTemplate }}>
      <div className="lv-name" style={{ paddingLeft: 32 + depth * 24 }}>
        {group?.status ? <StatusIcon type={group.status.type} color={group.status.color} /> : <StatusIcon type="open" color="var(--text-3)" />}
        <input
          ref={ref}
          className="lv-add-input"
          value={name}
          placeholder={placeholder}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') onDone();
          }}
          onBlur={() => !name && setTimeout(onDone, 150)}
        />
        <div className="lv-add-tools">
          <Popover content={() => <PeopleMenu selected={assignees} onChange={setAssignees} />}>
            <button className="lv-add-tool">{assignees.length ? <Avatar userId={assignees[0]} size={18} /> : <Icon name="UserPlus" size={14} />}</button>
          </Popover>
          <Popover content={(close) => <DateRangeMenu start={null} due={due} onChange={(_, d) => setDue(d)} onClose={close} />}>
            <button className="lv-add-tool">
              <Icon name="Calendar" size={14} />
              {due ? new Date(due).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : null}
            </button>
          </Popover>
          <Popover content={(close) => <PriorityMenu value={priority} onPick={(p) => (setPriority(p), close())} />}>
            <button className="lv-add-tool">
              <PriorityFlag priority={priority} />
            </button>
          </Popover>
          <button className="btn sm outline" onClick={onDone}>
            Cancel
          </button>
          <button className="btn sm primary" onClick={save} disabled={!name.trim()}>
            Save ↵
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rows

interface RowProps {
  task: Task;
  depth: number;
  columns: ColumnDef[];
  gridTemplate: string;
  spaceId: string | undefined;
  childrenOf: Map<string, Task[]>;
  expanded: Set<string>;
  toggleExpand: (id: string) => void;
  selected: Set<string>;
  toggleSelect: (id: string, e: React.MouseEvent) => void;
  addingSubtaskFor: string | null;
  setAddingSubtaskFor: (id: string | null) => void;
  location: Location;
  sortable?: boolean;
  groupKey?: string;
}

const TaskRowInner = memo(function TaskRowInner({
  task,
  depth,
  columns,
  gridTemplate,
  spaceId,
  hasChildren,
  childCount,
  isExpanded,
  toggleExpand,
  isSelected,
  toggleSelect,
  onAddSubtask,
  dragHandle,
}: {
  task: Task;
  depth: number;
  columns: ColumnDef[];
  gridTemplate: string;
  spaceId: string | undefined;
  hasChildren: boolean;
  childCount: number;
  isExpanded: boolean;
  toggleExpand: (id: string) => void;
  isSelected: boolean;
  toggleSelect: (id: string, e: React.MouseEvent) => void;
  onAddSubtask: () => void;
  dragHandle?: React.HTMLAttributes<HTMLElement>;
}) {
  const openTask = useOpenTask();
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDb((db) => effectiveStatuses(db, task.listId));
  const taskSpace = useDb((db) => db.lists[task.listId]?.spaceId);
  const [renaming, setRenaming] = useState(false);
  const closed = isCompleteType(status.type);
  return (
    <div
      className={clsx('lv-row', { selected: isSelected, closed })}
      style={{ gridTemplateColumns: gridTemplate }}
      onClick={() => !renaming && openTask(task.id)}
      onContextMenu={(e) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />)}
    >
      <div className="lv-name" style={{ paddingLeft: depth * 24 }}>
        <span className="lv-grip" {...dragHandle} onClick={(e) => e.stopPropagation()}>
          <GripVertical size={14} />
        </span>
        <span className="lv-check" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" className="checkbox" checked={isSelected} onChange={() => {}} onClick={(e) => toggleSelect(task.id, e)} />
        </span>
        <span className="lv-caret-slot">
          {hasChildren && (
            <button
              className={clsx('lv-caret', { open: isExpanded })}
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(task.id);
              }}
            >
              <ChevronRight size={14} />
            </button>
          )}
        </span>
        <Popover
          content={(close) => (
            <StatusMenu
              statuses={statuses}
              currentId={task.statusId}
              onPick={(s) => {
                updateTask(task.id, { statusId: s.id });
                close();
              }}
            />
          )}
        >
          <button className="lv-status-btn" title={status.name}>
            <StatusIcon type={status.type} color={status.color} progress={statusProgress(statuses, status)} />
          </button>
        </Popover>
        <TaskTypeIcon task={task} />
        {renaming ? (
          <input
            className="lv-rename"
            autoFocus
            defaultValue={task.name}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = (e.target as HTMLInputElement).value.trim();
                if (v && v !== task.name) updateTask(task.id, { name: v });
                setRenaming(false);
              }
              if (e.key === 'Escape') setRenaming(false);
            }}
            onBlur={(e) => {
              const v = e.target.value.trim();
              if (v && v !== task.name) updateTask(task.id, { name: v });
              setRenaming(false);
            }}
          />
        ) : (
          <span className="lv-title truncate">{task.name}</span>
        )}
        {childCount > 0 && (
          <button
            className="lv-subcount"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(task.id);
            }}
          >
            <SubtaskGlyph />
            {childCount}
          </button>
        )}
        {!renaming && (
          <span className="lv-hover-actions" onClick={(e) => e.stopPropagation()}>
            <Tooltip label="Add subtask">
              <button className="lv-mini" onClick={onAddSubtask}>
                <Plus size={13} />
              </button>
            </Tooltip>
            {(spaceId ?? taskSpace) && (
              <Popover content={() => <TagMenu spaceId={(spaceId ?? taskSpace)!} selected={task.tagIds} onChange={(ids) => updateTask(task.id, { tagIds: ids })} />}>
                <button className="lv-mini" title="Add tags">
                  <TagIcon size={13} />
                </button>
              </Popover>
            )}
            <Tooltip label="Rename">
              <button className="lv-mini" onClick={() => setRenaming(true)}>
                <Pencil size={13} />
              </button>
            </Tooltip>
          </span>
        )}
      </div>
      {columns.map((c) => (
        <div key={c.id} className="lv-cell" onClick={(e) => e.stopPropagation()}>
          <Cell task={task} column={c} spaceId={spaceId ?? taskSpace ?? ''} />
        </div>
      ))}
      <div className="lv-cell lv-row-end" onClick={(e) => e.stopPropagation()}>
        <Popover placement="bottom-end" content={(close) => <TaskContextMenu task={task} close={close} />}>
          <button className="icon-btn xs lv-row-more">
            <Ellipsis size={14} />
          </button>
        </Popover>
      </div>
    </div>
  );
});

export function SubtaskGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <circle cx="3" cy="2.6" r="1.6" />
      <path d="M3 4.4v2.4a2 2 0 0 0 2 2h2.2" />
      <circle cx="9" cy="8.8" r="1.6" />
    </svg>
  );
}

function TaskRow(props: RowProps) {
  const { task, depth, childrenOf, expanded, toggleExpand, selected, toggleSelect, addingSubtaskFor, setAddingSubtaskFor, sortable, groupKey } = props;
  const kids = childrenOf.get(task.id) ?? [];
  const count = useStore((s) => subtaskCount(s.db, task.id));
  const isExpanded = expanded.has(task.id);
  const sortableState = useSortable({ id: task.id, disabled: !sortable, data: { groupKey } });
  const style = sortable ? { transform: CSS.Translate.toString(sortableState.transform), transition: sortableState.transition, opacity: sortableState.isDragging ? 0.35 : 1 } : undefined;
  const onAddSubtask = useCallback(() => {
    setAddingSubtaskFor(task.id);
    if (!expanded.has(task.id)) toggleExpand(task.id);
  }, [task.id, expanded, toggleExpand, setAddingSubtaskFor]);
  return (
    <>
      <div ref={sortable ? sortableState.setNodeRef : undefined} style={style}>
        <TaskRowInner
          task={task}
          depth={depth}
          columns={props.columns}
          gridTemplate={props.gridTemplate}
          spaceId={props.spaceId}
          hasChildren={count > 0}
          childCount={count}
          isExpanded={isExpanded}
          toggleExpand={toggleExpand}
          isSelected={selected.has(task.id)}
          toggleSelect={toggleSelect}
          onAddSubtask={onAddSubtask}
          dragHandle={sortable ? { ...sortableState.attributes, ...sortableState.listeners } : undefined}
        />
      </div>
      {isExpanded &&
        kids.map((k) => <TaskRow key={k.id} {...props} task={k} depth={depth + 1} sortable={false} />)}
      {isExpanded && kids.length === 0 && count > 0 && <div className="lv-sub-hidden" style={{ paddingLeft: 56 + (depth + 1) * 24 }}>Subtasks are hidden by the current filters</div>}
      {addingSubtaskFor === task.id && (
        <InlineAdd
          location={props.location}
          listId={task.listId}
          parentId={task.id}
          depth={depth + 1}
          gridTemplate={props.gridTemplate}
          onDone={() => setAddingSubtaskFor(null)}
          placeholder="Subtask name"
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Column header

function ColumnHeader({ view, columns, gridTemplate, location }: { view: View; columns: ColumnDef[]; gridTemplate: string; location: Location }) {
  const s = view.settings;
  const setSort = (field: string, dir: 'asc' | 'desc' | null) => updateViewSettings(view.id, { sort: dir ? [{ field, dir }] : [] });
  const hide = (id: string) => updateViewSettings(view.id, { columns: s.columns.map((c) => (c.id === id ? { ...c, hidden: true } : c)) });
  const startResize = (id: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = id === 'name' ? (s.columns.find((c) => c.id === 'name')?.width ?? 420) : columns.find((c) => c.id === id)!.width;
    let w = startW;
    const el = (e.currentTarget as HTMLElement).closest('.lv-scroll') as HTMLElement | null;
    const onMove = (ev: PointerEvent) => {
      w = Math.max(id === 'name' ? 220 : 80, Math.min(id === 'name' ? 1000 : 600, startW + ev.clientX - startX));
      // live preview: rewrite the first matching track in every row of this view
      el?.querySelectorAll<HTMLElement>('.lv-row, .lv-colhead').forEach((row) => {
        const tracks = row.style.gridTemplateColumns.split(' ');
        const idx = id === 'name' ? 0 : 1 + columns.findIndex((c) => c.id === id);
        if (idx >= 0 && tracks[idx]) {
          tracks[idx] = `${w}px`;
          row.style.gridTemplateColumns = tracks.join(' ');
        }
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const has = s.columns.some((c) => c.id === id);
      const next: ViewColumn[] = has ? s.columns.map((c) => (c.id === id ? { ...c, width: Math.round(w) } : c)) : [{ id, width: Math.round(w) }, ...s.columns];
      updateViewSettings(view.id, { columns: next });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const sortOf = (id: string) => s.sort.find((x) => x.field === id)?.dir;
  return (
    <div className="lv-colhead" style={{ gridTemplateColumns: gridTemplate }}>
      <div className="lv-colhead-cell name">
        <button className="lv-colhead-btn" onClick={() => setSort('name', sortOf('name') === 'asc' ? 'desc' : sortOf('name') === 'desc' ? null : 'asc')}>
          Name {sortOf('name') === 'asc' ? <ArrowUp size={11} /> : sortOf('name') === 'desc' ? <ArrowDown size={11} /> : null}
        </button>
        <span className="lv-resize" onPointerDown={(e) => startResize('name', e)} />
      </div>
      {columns.map((c) => (
        <div key={c.id} className="lv-colhead-cell">
          <Popover
            content={(close) => (
              <Menu style={{ width: 220 }}>
                <MenuItem icon={<ArrowUp size={15} />} label="Sort ascending" checked={sortOf(c.id) === 'asc'} onClick={() => (setSort(c.id, 'asc'), close())} />
                <MenuItem icon={<ArrowDown size={15} />} label="Sort descending" checked={sortOf(c.id) === 'desc'} onClick={() => (setSort(c.id, 'desc'), close())} />
                {sortOf(c.id) && <MenuItem label="Clear sort" onClick={() => (setSort(c.id, null), close())} />}
                <MenuSeparator />
                <MenuItem icon={<EyeOff size={15} />} label="Hide column" onClick={() => (hide(c.id), close())} />
              </Menu>
            )}
          >
            <button className="lv-colhead-btn">
              <span className="truncate">{c.label}</span>
              {sortOf(c.id) === 'asc' ? <ArrowUp size={11} /> : sortOf(c.id) === 'desc' ? <ArrowDown size={11} /> : null}
            </button>
          </Popover>
          <span className="lv-resize" onPointerDown={(e) => startResize(c.id, e)} />
        </div>
      ))}
      <div className="lv-colhead-cell end">
        <Popover placement="bottom-end" content={() => <FieldsPanel location={location} settings={s} onChange={(c) => updateViewSettings(view.id, c)} />}>
          <button className="lv-addcol" title="Add column">
            <Plus size={12} />
          </button>
        </Popover>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Groups

function GroupHeaderLabel({ group }: { group: TaskGroup }) {
  if (group.status) return <StatusPill status={group.status} size="lg" />;
  const isUser = group.color && group.defaults.assigneeIds?.length;
  return (
    <span className="lv-group-label">
      {isUser ? <Avatar userId={group.key} size={20} /> : group.icon ? <Icon name={group.icon} size={15} color={group.color ?? 'var(--text-2)'} fill={group.icon === 'Flag' && group.color ? group.color : 'none'} /> : group.color ? <span className="dot" style={{ background: group.color }} /> : null}
      <span>{group.label}</span>
    </span>
  );
}

function GroupDrop({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `group:${id}`, data: { groupKey: id } });
  return (
    <div ref={setNodeRef} className={clsx('lv-group-body', { over: isOver })}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bulk actions

function BulkBar({ ids, clear, location }: { ids: string[]; clear: () => void; location: Location }) {
  const db = useDb((d) => d);
  const first = db.tasks[ids[0]];
  if (!first) return null;
  const statuses = effectiveStatuses(db, first.listId);
  const spaceId = db.lists[first.listId]?.spaceId;
  const apply = (changes: Partial<Task>) => ids.forEach((id) => updateTask(id, changes));
  return (
    <div className="bulk-bar">
      <span className="bulk-count">
        <input type="checkbox" className="checkbox" checked readOnly onClick={clear} />
        {ids.length} Task{ids.length > 1 ? 's' : ''} selected
      </span>
      <span className="bulk-sep" />
      <Popover placement="top" content={(close) => <StatusMenu statuses={statuses} onPick={(s) => (ids.forEach((id) => {
        const t = getDb().tasks[id];
        const target = effectiveStatuses(getDb(), t.listId).find((x) => x.name.toLowerCase() === s.name.toLowerCase());
        if (target) updateTask(id, { statusId: target.id });
      }), close())} />}>
        <button className="bulk-btn">Status</button>
      </Popover>
      <Popover placement="top" content={() => <PeopleMenu selected={[]} onChange={(u) => ids.forEach((id) => { const t = getDb().tasks[id]; updateTask(id, { assigneeIds: [...new Set([...t.assigneeIds, ...u])] }); })} />}>
        <button className="bulk-btn">Assignees</button>
      </Popover>
      <Popover placement="top" content={(close) => <DateRangeMenu start={null} due={null} onChange={(_, due) => apply({ dueDate: due })} onClose={close} />}>
        <button className="bulk-btn">Dates</button>
      </Popover>
      <Popover placement="top" content={(close) => <PriorityMenu value={null} onPick={(p) => (apply({ priority: p }), close())} />}>
        <button className="bulk-btn">Priority</button>
      </Popover>
      {spaceId && (
        <Popover placement="top" content={() => <TagMenu spaceId={spaceId} selected={[]} onChange={(tags) => ids.forEach((id) => { const t = getDb().tasks[id]; updateTask(id, { tagIds: [...new Set([...t.tagIds, ...tags])] }); })} />}>
          <button className="bulk-btn">Tags</button>
        </Popover>
      )}
      <Popover placement="top" content={(close) => <MoveTaskMenu taskIds={ids} onDone={() => (close(), clear())} />}>
        <button className="bulk-btn">Move</button>
      </Popover>
      <button className="bulk-btn" onClick={() => (ids.forEach((id) => duplicateTask(id)), toast(`${ids.length} task(s) duplicated`))}>
        Duplicate
      </button>
      <button
        className="bulk-btn danger"
        onClick={() => {
          if (confirm(`Delete ${ids.length} task(s)?`)) {
            ids.forEach((id) => deleteTask(id));
            clear();
          }
        }}
      >
        <Trash2 size={14} />
      </button>
      <button className="icon-btn sm" onClick={clear}>
        ✕
      </button>
      {location && null}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function ListView({ view, location }: { view: View; location: Location }) {
  const [search, setSearch] = useState('');
  const s = view.settings;
  const q = useViewQuery(view, location, search, { includeEmptyStatuses: !!s.showEmptyStatuses });
  const customFields = useDb((db) => db.customFields);
  const available = useDb((db) => fieldsForLocation(db, location).map((f) => f.id).join(','));
  const columns = useMemo(() => resolveColumns(s.columns, customFields, new Set(available.split(','))), [s.columns, customFields, available]);
  const nameWidth = s.columns.find((c) => c.id === 'name')?.width ?? 420;
  const gridTemplate = `${nameWidth}px ${columns.map((c) => `${c.width}px`).join(' ')} 48px minmax(0, 1fr)`;
  const spaceId = useDb((db) => (location.type === 'space' ? location.id : location.type === 'folder' ? db.folders[location.id]?.spaceId : location.type === 'list' ? db.lists[location.id]?.spaceId : undefined));

  const [expanded, setExpanded] = useState<Set<string>>(() => (s.subtasks === 'expanded' ? new Set(q.matched.map((t) => t.id)) : new Set()));
  useEffect(() => {
    setExpanded(s.subtasks === 'expanded' ? new Set(getDbTaskIdsWithChildren()) : new Set());
  }, [s.subtasks]);
  const toggleExpand = useCallback((id: string) => setExpanded((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  }), []);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const lastClicked = useRef<string | null>(null);
  const flatOrder = useMemo(() => q.groups.flatMap((g) => g.tasks.map((t) => t.id)), [q.groups]);
  const toggleSelect = useCallback(
    (id: string, e: React.MouseEvent) => {
      // read the anchor before updating state: updaters may run twice in StrictMode
      const anchor = e.shiftKey ? lastClicked.current : null;
      lastClicked.current = id;
      setSelected((prev) => {
        const n = new Set(prev);
        if (anchor) {
          const a = flatOrder.indexOf(anchor);
          const b = flatOrder.indexOf(id);
          if (a >= 0 && b >= 0) for (const x of flatOrder.slice(Math.min(a, b), Math.max(a, b) + 1)) n.add(x);
        } else if (n.has(id)) n.delete(id);
        else n.add(id);
        return n;
      });
    },
    [flatOrder],
  );
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelected(new Set());
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [addingIn, setAddingIn] = useState<string | null>(null);
  const [addingSubtaskFor, setAddingSubtaskFor] = useState<string | null>(null);
  const collapsed = new Set(s.collapsedGroups);
  const toggleGroup = (key: string) =>
    updateViewSettings(view.id, { collapsedGroups: collapsed.has(key) ? s.collapsedGroups.filter((k) => k !== key) : [...s.collapsedGroups, key] });

  // drag & drop
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [dragId, setDragId] = useState<string | null>(null);
  // scrolled sideways: the sticky name side gets an opaque background (views.css)
  const [scrolledX, setScrolledX] = useState(false);
  const dndEnabled = s.sort.length === 0 && !search;
  const onDragStart = (e: DragStartEvent) => setDragId(String(e.active.id));
  const onDragEnd = (e: DragEndEvent) => {
    setDragId(null);
    if (!e.over) return;
    const activeId = String(e.active.id);
    const overGroupKey = (e.over.data.current?.groupKey as string | undefined) ?? undefined;
    const group = q.groups.find((g) => g.key === overGroupKey);
    if (!group) return;
    const list = group.tasks.filter((t) => t.id !== activeId);
    let idx = list.length;
    if (!String(e.over.id).startsWith('group:')) {
      idx = list.findIndex((t) => t.id === e.over!.id);
      const activeRect = e.active.rect.current.translated;
      const overRect = e.over.rect;
      if (activeRect && activeRect.top > overRect.top + overRect.height / 2) idx += 1;
      if (idx < 0) idx = list.length;
    }
    const before = list[idx - 1];
    const after = list[idx];
    const task = getDb().tasks[activeId];
    if (!task) return;
    const fromGroup = q.groups.find((g) => g.tasks.some((t) => t.id === activeId));
    const changes: Partial<Task> = {};
    if (fromGroup && fromGroup.key !== group.key) {
      const d = group.defaults;
      if (group.status) {
        const target = effectiveStatuses(getDb(), task.listId).find((x) => x.name.toLowerCase() === group.status!.name.toLowerCase());
        if (target) changes.statusId = target.id;
      }
      if ('priority' in d) changes.priority = d.priority ?? null;
      if (d.assigneeIds) changes.assigneeIds = d.assigneeIds.length ? [...new Set([...task.assigneeIds.filter((u) => u !== fromGroup.key), ...d.assigneeIds])] : [];
      if ('taskTypeId' in d) changes.taskTypeId = d.taskTypeId ?? null;
      if (d.customFields) changes.customFields = { ...task.customFields, ...d.customFields };
    }
    reorderTask(activeId, before, after, changes);
  };

  const dragTask = dragId ? getDb().tasks[dragId] : null;

  return (
    <div className="lv">
      <ViewToolbar view={view} location={location} search={search} onSearch={setSearch} onAddTask={() => setAddingIn(q.groups[0]?.key ?? 'all')} />
      <div className={clsx('lv-scroll', { 'scrolled-x': scrolledX })} onScroll={(e) => setScrolledX(e.currentTarget.scrollLeft > 0)}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragId(null)}>
          <div className="lv-inner" style={{ minWidth: 28 + nameWidth + columns.reduce((a, c) => a + c.width, 0) + 48 }}>
            {q.groups.map((g) => {
              const isCollapsed = collapsed.has(g.key);
              return (
                <section key={g.key} className="lv-group">
                  <div className="lv-group-head">
                    <button className={clsx('lv-group-caret', { open: !isCollapsed })} onClick={() => toggleGroup(g.key)}>
                      <ChevronRight size={14} />
                    </button>
                    <GroupHeaderLabel group={g} />
                    <span className="lv-group-count">{g.tasks.length}</span>
                    <Popover
                      content={(close) => (
                        <Menu>
                          <MenuItem label={isCollapsed ? 'Expand group' : 'Collapse group'} onClick={() => (toggleGroup(g.key), close())} />
                          <MenuItem label="Collapse all groups" onClick={() => (updateViewSettings(view.id, { collapsedGroups: q.groups.map((x) => x.key) }), close())} />
                          <MenuItem label="Expand all groups" onClick={() => (updateViewSettings(view.id, { collapsedGroups: [] }), close())} />
                          {g.status && location.type !== 'workspace' && (
                            <>
                              <MenuSeparator />
                              <MenuItem
                                icon={<Settings2 size={15} />}
                                label="Edit statuses"
                                onClick={() => {
                                  close();
                                  openDialog({ kind: 'statuses', target: location as { type: 'space' | 'folder' | 'list'; id: string } });
                                }}
                              />
                            </>
                          )}
                        </Menu>
                      )}
                    >
                      <button className="icon-btn xs lv-group-more">
                        <Ellipsis size={14} />
                      </button>
                    </Popover>
                    <button className="lv-group-add" onClick={() => setAddingIn(g.key)}>
                      <Plus size={13} /> Add Task
                    </button>
                  </div>
                  {!isCollapsed && (
                    <>
                      <ColumnHeader view={view} columns={columns} gridTemplate={gridTemplate} location={location} />
                      <GroupDrop id={g.key}>
                        <SortableContext items={g.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                          {g.tasks.map((t) => (
                            <TaskRow
                              key={t.id}
                              task={t}
                              depth={0}
                              columns={columns}
                              gridTemplate={gridTemplate}
                              spaceId={spaceId}
                              childrenOf={q.children}
                              expanded={expanded}
                              toggleExpand={toggleExpand}
                              selected={selected}
                              toggleSelect={toggleSelect}
                              addingSubtaskFor={addingSubtaskFor}
                              setAddingSubtaskFor={setAddingSubtaskFor}
                              location={location}
                              sortable={dndEnabled}
                              groupKey={g.key}
                            />
                          ))}
                        </SortableContext>
                        {addingIn === g.key ? (
                          <InlineAdd group={g} location={location} depth={0} gridTemplate={gridTemplate} onDone={() => setAddingIn(null)} />
                        ) : (
                          <button className="lv-add" onClick={() => setAddingIn(g.key)}>
                            <span className="lv-add-label">
                              <Plus size={14} /> Add Task
                            </span>
                          </button>
                        )}
                      </GroupDrop>
                    </>
                  )}
                </section>
              );
            })}
            {q.groups.length === 0 && (
              <div className="lv-empty">
                <div className="lv-empty-title">No tasks found</div>
                <div className="faint">Try changing filters, or create a task.</div>
                <button className="btn primary" style={{ marginTop: 12 }} onClick={() => setAddingIn('all')}>
                  <Plus size={14} /> Add Task
                </button>
                {addingIn === 'all' && <InlineAdd location={location} depth={0} gridTemplate={gridTemplate} onDone={() => setAddingIn(null)} />}
              </div>
            )}
          </div>
          <DragOverlay dropAnimation={null}>
            {dragTask ? (
              <div className="lv-drag-ghost">
                <StatusIcon type="custom" color="var(--text-3)" />
                <span className="truncate">{dragTask.name}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
      {selected.size > 0 && <BulkBar ids={[...selected].filter((id) => getDb().tasks[id])} clear={() => setSelected(new Set())} location={location} />}
    </div>
  );
}

function getDbTaskIdsWithChildren(): string[] {
  const out = new Set<string>();
  for (const t of Object.values(getDb().tasks)) if (t.parentId) out.add(t.parentId);
  return [...out];
}
